import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { Paths } from "src/paths";
import Select from "react-select";
import GenericButton from "../../../../../../../components/form/GenericButton.tsx";
import { useSnapshot } from "valtio/index";
import { engagementStore } from "../../../../../engagementStore.ts";
import { useEffectAfterMount } from "../../../../../../../shared/hooks/useEffectAfterMount.tsx";
import { engagementEvents } from "../../../../../engagementEvents.ts";
import { EngagementService } from "@services/engagements/engagementService.ts";
import { OrganizationService } from "@services/organizations/OrganizationService.ts";
import { Organization } from "@models/organization.model.ts";
import { appStore } from "@store/appStore.ts";
import Loader from "../../../../../../../components/common/loader/loader.tsx";
import OrganizationPicker from "@/shared/components/OrganizationPicker/OrganizationPicker";

const CreateEngagement = () => {
  useSnapshot(engagementStore);
  useSnapshot(engagementEvents);

  const engagementService = new EngagementService();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [engagementType, setEngagementType] = useState<string | null>("REACH");
  const [name, setName] = useState("");

  // A brand-new client won't be in the picker (it reads the user's permitted
  // orgs, which don't refresh mid-session), so the created org is held here and
  // shown in the picker's place.
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgSite, setNewOrgSite] = useState("");
  const [createdOrg, setCreatedOrg] = useState<Organization | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetOrgChoice = () => {
    setIsCreatingOrg(false);
    setNewOrgName("");
    setNewOrgSite("");
    setCreatedOrg(null);
    setSelectedOrgId(null);
    setError(null);
  };

  const createOrganization = async () => {
    const organization = new Organization();
    organization.name = newOrgName.trim();
    organization.site_url = newOrgSite.trim();
    const created = await new OrganizationService().createOrganization(organization);
    if (!created?.id) throw new Error("Organization was not created");
    setCreatedOrg(created);
    setSelectedOrgId(created.id);
    return created.id;
  };

  const createEngagement = async (clientOrgId: number) => {
    const created = await engagementService.createEngagement(
      appStore.loggedInUser.organization.id,
      clientOrgId,
      engagementType as string,
      { name: name.trim() }
    );
    engagementEvents.send_event("ENGAGEMENT_CREATED");
    return created;
  };

  useEffectAfterMount(() => {
    setShowModal(true);
  }, [engagementEvents.NEW_ENGAGEMENT_CLICKED]);

  function handleCloseClicked() {
    setShowModal(false);
    setName("");
    resetOrgChoice();
  }

  async function handleSaveClicked() {
    if (engagementType == null) return;
    setError(null);
    setIsLoading(true);
    try {
      // Creating the client first means a failure there never leaves a
      // half-made engagement pointed at nothing.
      const clientOrgId = isCreatingOrg && !createdOrg ? await createOrganization() : selectedOrgId;
      if (clientOrgId == null) {
        setError("Choose an organization for this engagement.");
        return;
      }
      const created = await createEngagement(clientOrgId);
      setShowModal(false);
      setName("");
      resetOrgChoice();
      // Straight into the new engagement — there is nothing to do with it from
      // the list, and the next step is always inside it.
      if (created?.id != null && created.id !== -1) {
        engagementStore.selectedEngagement = created;
        navigate(`${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${created.id}/todos`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the engagement");
    } finally {
      setIsLoading(false);
    }
  }

  const hasClient = isCreatingOrg ? newOrgName.trim().length > 0 : selectedOrgId != null;
  const canSave = hasClient && name.trim().length > 0;

  return (
    <>
      {isLoading && <Loader />}
      <Modal show={showModal} onHide={handleCloseClicked}>
        <Modal.Header closeButton>
          <Modal.Title as="h6">Create Engagement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="fw-semibold mb-2">Engagement name</p>
          <input
            className="form-control"
            placeholder="e.g. Johnston Community College F26"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />

          <p className="fw-semibold mt-4 mb-2">Type of engagement</p>
          <Select<{ value: string; label: string }>
            isSearchable
            name="team"
            defaultValue={{ value: "REACH", label: "Reach" }}
            options={[
              { value: "CREATE", label: "Create" },
              { value: "REACH", label: "Reach" },
            ]}
            onChange={(option) => setEngagementType(option?.value ?? null)}
          />

          <div className="d-flex align-items-center mt-4 mb-2">
            <p className="fw-semibold mb-0">
              {isCreatingOrg ? "New organization" : "Select an organization"}
            </p>
            <button
              type="button"
              className="btn btn-sm btn-link ms-auto p-0 text-decoration-none"
              onClick={() => {
                resetOrgChoice();
                setIsCreatingOrg((v) => !v);
              }}
              style={{ fontSize: "0.78rem" }}
            >
              {isCreatingOrg ? "Pick an existing one" : "Create a new one"}
            </button>
          </div>

          {!isCreatingOrg && (
            <OrganizationPicker value={selectedOrgId} onChange={setSelectedOrgId} />
          )}

          {isCreatingOrg && !createdOrg && (
            <>
              <input
                className="form-control mb-2"
                placeholder="Organization name"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
              />
              <input
                className="form-control"
                placeholder="Website (optional)"
                value={newOrgSite}
                onChange={(e) => setNewOrgSite(e.target.value)}
              />
              <div className="text-muted mt-2" style={{ fontSize: "0.75rem" }}>
                Branding, data files and the rest are set up from the engagement once it exists.
              </div>
            </>
          )}

          {createdOrg && (
            <div className="border rounded p-2 d-flex align-items-center gap-2">
              <i className="bi bi-check-circle text-success" />
              <span className="fw-semibold">{createdOrg.name}</span>
              <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                created
              </span>
            </div>
          )}

          {error && <div className="alert alert-danger py-2 small mt-3 mb-0">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <GenericButton
            onClick={handleCloseClicked}
            isPrimary={false}
            label="Cancel"
            type="button"
            className="me-2"
          />
          <GenericButton
            onClick={handleSaveClicked}
            label="Save"
            type="submit"
            disabled={!canSave || isLoading}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateEngagement;
